import { AnimatePresence, motion } from 'framer-motion'
import { ChevronDown, Languages, Menu, X } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { Link, NavLink, useLocation } from 'react-router-dom'
import { useLanguage } from '../../context/LanguageContext'

const primaryRoutes = [
  { key: 'home', to: '/' },
  { key: 'about', to: '/hakkimda' },
]

const expertiseRoutes = [
  { key: 'services', to: '/hizmetler' },
  { key: 'analyses', to: '/analizler' },
  { key: 'research', to: '/arastirmalar' },
]

const contactRoute = { key: 'contact', to: '/iletisim' }
const referencesRoute = { key: 'references', to: '/referanslar' }

const secondaryRoutes = [
  { key: 'blog', to: '/blog' },
  { key: 'media', to: '/medya' },
  { key: 'lifestyle', to: '/yasam-stili' },
]

function Brand() {
  const { language } = useLanguage()

  return (
    <Link to="/" className="group flex min-h-11 items-center gap-3" aria-label="Gürkan Kavrazlı ana sayfa">
      <img
        src="/images/icongk.jpeg"
        alt="Gürkan Kavrazlı logosu"
        width="56"
        height="56"
        className="h-14 w-14 shrink-0 rounded-xl border border-brand-navy/10 bg-white object-contain p-1 shadow-sm transition-transform duration-200 group-hover:-rotate-1 group-hover:scale-105"
      />
      <span className="hidden leading-tight sm:block">
        <strong className="block font-display text-sm text-brand-navy">Gürkan Kavrazlı</strong>
        <span lang={language} className="text-[0.68rem] font-semibold uppercase tracking-[0.14em] text-slate-500">
          {language === 'tr' ? 'Lojistik Stratejisti' : 'Logistics Strategist'}
        </span>
      </span>
    </Link>
  )
}

export function Header() {
  const { content, language, toggleLanguage } = useLanguage()
  const [mobileOpen, setMobileOpen] = useState(false)
  const [expertiseOpen, setExpertiseOpen] = useState(false)
  const [moreOpen, setMoreOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const expertiseMenuRef = useRef(null)
  const moreMenuRef = useRef(null)
  const location = useLocation()

  useEffect(() => {
    setMobileOpen(false)
    setExpertiseOpen(false)
    setMoreOpen(false)
  }, [location.pathname])

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 18)
    handleScroll()
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  useEffect(() => {
    if (!expertiseOpen && !moreOpen) return undefined

    const handlePointerDown = (event) => {
      if (expertiseOpen && !expertiseMenuRef.current?.contains(event.target)) setExpertiseOpen(false)
      if (!moreMenuRef.current?.contains(event.target)) setMoreOpen(false)
    }
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setExpertiseOpen(false)
        setMoreOpen(false)
      }
    }

    document.addEventListener('pointerdown', handlePointerDown)
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [expertiseOpen, moreOpen])

  const isExpertiseActive = expertiseRoutes.some((route) => location.pathname.startsWith(route.to))
  const isSecondaryActive = secondaryRoutes.some((route) => location.pathname.startsWith(route.to))

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        scrolled ? 'border-b border-brand-navy/8 bg-white/92 shadow-[0_8px_30px_rgba(10,35,66,.06)] backdrop-blur-xl' : 'bg-white/80 backdrop-blur-lg'
      }`}
    >
      <div className="site-container flex h-20 items-center justify-between gap-4">
        <Brand />

        <nav className="hidden items-center gap-5 lg:flex" aria-label="Ana navigasyon">
          {primaryRoutes.map((route) => (
            <NavLink
              key={route.key}
              to={route.to}
              end={route.to === '/'}
              className={({ isActive }) => `nav-link min-h-11 ${isActive ? 'active' : ''}`}
            >
              {content.nav[route.key]}
            </NavLink>
          ))}

          <div ref={expertiseMenuRef} className="relative">
            <button
              type="button"
              className={`nav-link min-h-11 gap-1 ${isExpertiseActive ? 'active' : ''}`}
              onClick={() => {
                setExpertiseOpen((current) => !current)
                setMoreOpen(false)
              }}
              aria-expanded={expertiseOpen}
              aria-haspopup="menu"
            >
              {language === 'tr' ? 'Uzmanlıklar' : 'Expertise'}
              <ChevronDown className={`transition-transform ${expertiseOpen ? 'rotate-180' : ''}`} size={15} />
            </button>
            <AnimatePresence>
              {expertiseOpen && (
                <motion.div
                  role="menu"
                  initial={{ opacity: 0, y: 8, scale: 0.98 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 8, scale: 0.98 }}
                  transition={{ duration: 0.18 }}
                  className="absolute left-0 top-[calc(100%+.65rem)] w-64 overflow-hidden rounded-2xl border border-brand-navy/10 bg-white p-2 shadow-[0_20px_55px_rgba(10,35,66,.16)]"
                >
                  {expertiseRoutes.map((route) => (
                    <NavLink
                      role="menuitem"
                      key={route.key}
                      to={route.to}
                      className={({ isActive }) =>
                        `flex min-h-11 items-center rounded-xl px-3 text-sm font-semibold transition-colors ${
                          isActive ? 'bg-brand-orange-soft text-brand-orange' : 'text-slate-600 hover:bg-brand-mist hover:text-brand-navy'
                        }`
                      }
                    >
                      {content.nav[route.key]}
                    </NavLink>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <div ref={moreMenuRef} className="relative">
            <button
              type="button"
              className={`nav-link min-h-11 gap-1 ${isSecondaryActive ? 'active' : ''}`}
              onClick={() => {
                setMoreOpen((current) => !current)
                setExpertiseOpen(false)
              }}
              aria-expanded={moreOpen}
              aria-haspopup="menu"
            >
              {language === 'tr' ? 'Medya & Yaşam' : 'Media & Lifestyle'}
              <ChevronDown className={`transition-transform ${moreOpen ? 'rotate-180' : ''}`} size={15} />
            </button>
            <AnimatePresence>
              {moreOpen && (
                <motion.div
                  role="menu"
                  initial={{ opacity: 0, y: 8, scale: 0.98 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 8, scale: 0.98 }}
                  transition={{ duration: 0.18 }}
                  className="absolute right-0 top-[calc(100%+.65rem)] w-64 overflow-hidden rounded-2xl border border-brand-navy/10 bg-white p-2 shadow-[0_20px_55px_rgba(10,35,66,.16)]"
                >
                  {secondaryRoutes.map((route) => (
                    <NavLink
                      role="menuitem"
                      key={route.key}
                      to={route.to}
                      className={({ isActive }) =>
                        `flex min-h-11 items-center rounded-xl px-3 text-sm font-semibold transition-colors ${
                          isActive ? 'bg-brand-orange-soft text-brand-orange' : 'text-slate-600 hover:bg-brand-mist hover:text-brand-navy'
                        }`
                      }
                    >
                      {content.nav[route.key]}
                    </NavLink>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <NavLink
            to={referencesRoute.to}
            className={({ isActive }) => `nav-link min-h-11 ${isActive ? 'active' : ''}`}
          >
            {content.nav[referencesRoute.key]}
          </NavLink>

          <NavLink
            to={contactRoute.to}
            className={({ isActive }) => `nav-link min-h-11 ${isActive ? 'active' : ''}`}
          >
            {content.nav[contactRoute.key]}
          </NavLink>
        </nav>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={toggleLanguage}
            className="inline-flex min-h-11 items-center gap-2 rounded-full border border-brand-navy/12 bg-white px-3 text-xs font-extrabold text-brand-navy transition-colors hover:border-brand-orange hover:text-brand-orange"
            aria-label={language === 'tr' ? 'Switch to English' : 'Türkçeye geç'}
          >
            <Languages size={16} aria-hidden="true" />
            {language === 'tr' ? 'EN' : 'TR'}
          </button>
          <button
            type="button"
            className="grid h-11 w-11 place-items-center rounded-full bg-brand-navy text-white lg:hidden"
            onClick={() => setMobileOpen((current) => !current)}
            aria-expanded={mobileOpen}
            aria-label={mobileOpen ? 'Menüyü kapat' : 'Menüyü aç'}
          >
            {mobileOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {mobileOpen && (
          <motion.nav
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25 }}
            className="overflow-hidden border-t border-brand-navy/8 bg-white lg:hidden"
            aria-label="Mobil navigasyon"
          >
            <div className="site-container grid gap-1 py-4">
              {primaryRoutes.map((route) => (
                <NavLink
                  key={route.key}
                  to={route.to}
                  end={route.to === '/'}
                  className={({ isActive }) =>
                    `flex min-h-12 items-center rounded-xl px-4 text-sm font-bold transition-colors ${
                      isActive ? 'bg-brand-orange-soft text-brand-orange' : 'text-brand-navy hover:bg-brand-mist'
                    }`
                  }
                >
                  {content.nav[route.key]}
                </NavLink>
              ))}
              <p className="px-4 pb-1 pt-3 text-xs font-extrabold uppercase tracking-[0.14em] text-slate-400">
                {language === 'tr' ? 'Uzmanlıklar' : 'Expertise'}
              </p>
              {expertiseRoutes.map((route) => (
                <NavLink
                  key={route.key}
                  to={route.to}
                  className={({ isActive }) =>
                    `ml-3 flex min-h-11 items-center rounded-xl px-4 text-sm font-bold transition-colors ${
                      isActive ? 'bg-brand-orange-soft text-brand-orange' : 'text-brand-navy hover:bg-brand-mist'
                    }`
                  }
                >
                  {content.nav[route.key]}
                </NavLink>
              ))}
              <p className="px-4 pb-1 pt-3 text-xs font-extrabold uppercase tracking-[0.14em] text-slate-400">
                {language === 'tr' ? 'Medya & Yaşam' : 'Media & Lifestyle'}
              </p>
              {secondaryRoutes.map((route) => (
                <NavLink
                  key={route.key}
                  to={route.to}
                  className={({ isActive }) =>
                    `ml-3 flex min-h-11 items-center rounded-xl px-4 text-sm font-bold transition-colors ${
                      isActive ? 'bg-brand-orange-soft text-brand-orange' : 'text-brand-navy hover:bg-brand-mist'
                    }`
                  }
                >
                  {content.nav[route.key]}
                </NavLink>
              ))}
              <NavLink
                to={referencesRoute.to}
                className={({ isActive }) =>
                  `mt-2 flex min-h-12 items-center rounded-xl px-4 text-sm font-bold transition-colors ${
                    isActive ? 'bg-brand-orange-soft text-brand-orange' : 'text-brand-navy hover:bg-brand-mist'
                  }`
                }
              >
                {content.nav[referencesRoute.key]}
              </NavLink>
              <NavLink
                to={contactRoute.to}
                className={({ isActive }) =>
                  `mt-2 flex min-h-12 items-center rounded-xl px-4 text-sm font-bold transition-colors ${
                    isActive ? 'bg-brand-orange-soft text-brand-orange' : 'text-brand-navy hover:bg-brand-mist'
                  }`
                }
              >
                {content.nav[contactRoute.key]}
              </NavLink>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  )
}
